import React, { useEffect, useRef, useState } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { API } from '../utils/apiBase';

const fmtAud = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Number(n) || 0);

/**
 * SearchPanel — slide-down predictive search.
 *
 * Triggered by clicking the search icon in the header. Debounces input by
 * 200ms, hits /api/search?q=, renders results inline. Esc closes.
 */
export const SearchPanel = ({ open, onClose }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem('tt:recent-search') || '[]'); } catch { return []; }
  });
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
    else { setQ(''); setResults([]); }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults([]); return undefined; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/api/search?q=${encodeURIComponent(q.trim())}&limit=8`);
        const data = await r.json();
        setResults(data.items || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  const saveRecent = (term) => {
    const next = [term, ...recent.filter((r) => r !== term)].slice(0, 5);
    setRecent(next);
    try { localStorage.setItem('tt:recent-search', JSON.stringify(next)); } catch { /* ignore */ }
  };

  const handleResultClick = () => {
    if (q.trim()) saveRecent(q.trim());
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="search-panel">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#33200F]/40 backdrop-blur-sm" onClick={onClose} />
      {/* Panel */}
      <div className="relative bg-[#F5EDDD] border-b border-[#C9B596] shadow-xl">
        <div className="max-w-4xl mx-auto px-6 py-5">
          <div className="flex items-center gap-3 border-b border-[#C9B596] pb-3">
            <Search className="h-5 w-5 text-[#A07A4F]" strokeWidth={1.5} />
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search beds, sofas, lighting…"
              className="flex-1 h-10 bg-transparent text-[18px] text-[#33200F] focus:outline-none placeholder:text-[#A07A4F]/60"
              data-testid="search-input"
            />
            {loading && <Loader2 className="h-4 w-4 animate-spin text-[#A07A4F]" strokeWidth={1.4} />}
            <button
              onClick={onClose}
              aria-label="Close search"
              data-testid="search-close"
              className="h-8 w-8 inline-flex items-center justify-center text-[#5B3A24] hover:text-[#33200F]"
            >
              <X className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>

          <div className="mt-4 max-h-[60vh] overflow-y-auto">
            {/* Recent searches when input is empty */}
            {!q.trim() && recent.length > 0 && (
              <div className="mb-4">
                <p className="text-[10px] tracking-[0.22em] uppercase text-[#A07A4F] mb-2">Recent</p>
                <div className="flex flex-wrap gap-2">
                  {recent.map((r) => (
                    <button
                      key={r}
                      onClick={() => setQ(r)}
                      className="text-[12px] px-3 py-1.5 border border-[#C9B596] text-[#5B3A24] hover:border-[#33200F] hover:text-[#33200F] transition-colors"
                    >{r}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Live results */}
            {q.trim() && !loading && results.length === 0 && (
              <p className="text-[14px] text-[#5B3A24] italic py-8 text-center">
                No matches for &quot;{q}&quot;. Try searching for &quot;sofa&quot;, &quot;bouclé&quot;, or &quot;pendant&quot;.
              </p>
            )}

            {results.length > 0 && (
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {results.map((p) => (
                  <li key={p.id}>
                    <Link
                      to={`/products/${p.id}`}
                      onClick={handleResultClick}
                      data-testid={`search-result-${p.id}`}
                      className="flex items-center gap-4 p-2 border border-transparent hover:border-[#C9B596] hover:bg-[#EDE3CF]/50 transition-colors"
                    >
                      <div className="h-14 w-14 bg-[#EDE3CF] flex-shrink-0 overflow-hidden">
                        {p.primary_image && (
                          <img src={p.primary_image} alt="" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[15px] text-[#33200F] truncate" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{p.name}</div>
                        {p.tagline && <div className="text-[11px] text-[#A07A4F] truncate">{p.tagline}</div>}
                        <div className="text-[12px] text-[#5B3A24]">{fmtAud(p.price)} {p.currency}</div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPanel;
